// Libs
import { ChangeEvent, ReactElement, useEffect, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { useFormikContext } from 'formik';
import { KeyboardBackspaceRounded } from '@mui/icons-material';
import keys from 'lodash/keys';
import Grid from '@mui/material/Grid';

// Components
import { Button, LoadingCards } from 'components';
import { ILabelType, MultiTypeahead } from 'components/MultiTypeahead';

// Styles
import styles from '../Wizard.module.scss';

// Core
import { ButtonVariant, IDictionary, IUserProfile, useAppDispatch } from 'core';

// Store
import { specialtiesSelector, userProfileSpecialtyIdsSelector } from 'store/profile/selectors';
import { getSpecialties } from 'store/profile/actions';
import { IWizardProps } from '../types';

type IProps = Pick<IWizardProps, 'back' | 'submit'>;

export const Specialties = ({ back, submit }: IProps): ReactElement => {
  const dispatch = useAppDispatch();
  const { isSubmitting, isValid, setFieldValue } = useFormikContext<IUserProfile>();

  // Selectors.
  const specialties: IDictionary<string> = useSelector(specialtiesSelector);
  const specialtyIds: string[] = useSelector(userProfileSpecialtyIdsSelector);

  const formatSpecialtiesForTypeahead = (id: string): ILabelType => ({ label: specialties?.[id], id });

  // Format the specialty options for the dropdown.
  const specialtyOptions: ILabelType[] = useMemo(() => {
    const idKeys: string[] = keys(specialties);
    const options: ILabelType[] = idKeys?.map(formatSpecialtiesForTypeahead);

    return options || [];
  }, [specialties]);

  // Format the initialValues for the typeahead.
  const formattedInitialSpecialties = specialtyIds?.map(formatSpecialtiesForTypeahead);

  useEffect(() => {
    dispatch(getSpecialties());
  }, [dispatch]);

  const onChangeSpecialty = (option: string): void => {
    setFieldValue('specialtyIds', option);
  };

  return (
    <div className={styles['boards-repeater']}>
      <h5>What is your specialty or area of practice? Select all that apply.</h5>
      <br />
      {!specialtyOptions?.length ? (
        <LoadingCards count={1} />
      ) : (
        <MultiTypeahead
          fullWidth
          id="specialtyIds"
          initialOptions={formattedInitialSpecialties}
          onInputChange={(_: ChangeEvent<HTMLInputElement>, option: string): void => onChangeSpecialty(option)}
          options={specialtyOptions}
          placeholder="Select Specialty/Area of Practice"
          setFieldValue={setFieldValue}
        />
      )}
      <Grid container sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', my: 3 }}>
        <Grid>
          <Button
            className={styles['wizard-button']}
            sx={{ marginRight: 3 }}
            variant={ButtonVariant.Tertiary}
            onClick={back}
          >
            <KeyboardBackspaceRounded /> Back
          </Button>
        </Grid>
        <Grid>
          <Button
            className={styles['wizard-button']}
            disabled={isSubmitting || !isValid}
            isSubmitting={isSubmitting}
            onClick={submit}
            variant={ButtonVariant.Primary}
          >
            Submit
          </Button>
        </Grid>
      </Grid>
    </div>
  );
};
